import { MAX_PER_LOBBY } from "@game/config";
import { DbConnection } from "./stdb_bindings";
import type {
  RealtimeGameAsteroid,
  RealtimeGameBullet,
  RealtimeGameMine,
  RealtimeGameModeState,
  RealtimeGamePellet,
  RealtimeMobaBase,
  RealtimeMobaNpc,
  RealtimePerformanceMetrics,
  RealtimeGamePlayer,
  RealtimeLobbySummary,
  RealtimePartyState,
} from "./realtime.types";

export interface StdbConnectionConfig {
  uri: string;
  database: string;
  token?: string;
}

export interface StdbConnectedEvent {
  identity: string;
  token: string;
}

export interface StdbLobbyStateEvent {
  lobbyId: string;
  players: Array<{
    id: string;
    name: string;
    color?: string;
    ready?: boolean;
  }>;
  started: boolean;
  secondsUntilStart: number;
  teamMode?: string;
}

export interface StdbLobbyListEvent {
  lobbies: RealtimeLobbySummary[];
}

export interface StdbPartyStateEvent {
  party: RealtimePartyState | null;
}

export interface StdbGameStateEvent {
  players: RealtimeGamePlayer[];
  bullets: RealtimeGameBullet[];
  asteroids: RealtimeGameAsteroid[];
  pellets: RealtimeGamePellet[];
  mines: RealtimeGameMine[];
  timestamp: number;
  sequence: number;
  gameModeState?: RealtimeGameModeState;
  mobaState?: {
    bases: RealtimeMobaBase[];
    npcs: RealtimeMobaNpc[];
    waveNumber: number;
    teamScores: Record<number, number>;
  };
  performance?: RealtimePerformanceMetrics;
}

export interface StdbBindingsBridge {
  connect(config: StdbConnectionConfig): Promise<void>;
  disconnect(): Promise<void>;
  leaveLobby(): Promise<void>;
  debugPelletBoost(): Promise<void>;
  debugToggleInvincibility(): Promise<void>;
  debugInstantDeath(): Promise<void>;
  debugInstantVictory(): Promise<void>;
  requestLobbyList(): Promise<void>;
  deployMine(): Promise<void>;
  joinLobby(request: {
    lobbyId: string;
    playerName: string;
    primaryWeapon?: string;
  }): Promise<void>;
  setLoadout(request: { primaryWeapon: string }): Promise<void>;
  partyCreate(request: { playerName: string }): Promise<void>;
  partyJoinCode(request: {
    joinCode: string;
    playerName: string;
  }): Promise<void>;
  partyToggleReady(): Promise<void>;
  partyLeave(): Promise<void>;
  partyQueue(): Promise<void>;
  submitInput(request: {
    lobbyId: string;
    sequence: number;
    input: {
      keyForward: boolean;
      keyBack: boolean;
      keyLeft: boolean;
      keyRight: boolean;
      keyBoost: boolean;
      keyShift: boolean;
      mouseScreenX: number;
      mouseScreenY: number;
      canvasWidth: number;
      canvasHeight: number;
      mouseShoot: boolean;
    };
  }): Promise<void>;
  onConnected(handler: (event: StdbConnectedEvent) => void): () => void;
  onDisconnected(handler: () => void): () => void;
  onConnectError(handler: (error: Error) => void): () => void;
  onLobbyState(handler: (event: StdbLobbyStateEvent) => void): () => void;
  onLobbyList(handler: (event: StdbLobbyListEvent) => void): () => void;
  onPartyState(handler: (event: StdbPartyStateEvent) => void): () => void;
  onGameState(handler: (event: StdbGameStateEvent) => void): () => void;
}

const BETTER_AUTH_SESSION_PLACEHOLDER = "better-auth-session";

function emitToListeners<T>(
  listeners: Set<(event: T) => void>,
  event: T,
): void {
  for (const listener of listeners) {
    listener(event);
  }
}

function normalizeConnectionToken(token?: string): string | undefined {
  const normalized = token?.trim();
  if (!normalized || normalized === BETTER_AUTH_SESSION_PLACEHOLDER) {
    return undefined;
  }
  return normalized;
}

function isInvalidTokenConnectError(error: Error): boolean {
  const message = error.message.toLowerCase();
  return (
    message.includes("invalidtoken") ||
    message.includes("invalid token") ||
    message.includes("tokenerror")
  );
}

function toLobbyStatus(
  started: boolean,
  secondsUntilStart: number,
): RealtimeLobbySummary["status"] {
  if (started) {
    return "in_progress";
  }
  if (secondsUntilStart > 0) {
    return "countdown";
  }
  return "forming";
}

function resolveCreatedAt(value: unknown): number {
  return typeof value === "number" ? value : Date.now();
}

function inferLobbyTeamModeFromId(lobbyId: string): string {
  if (lobbyId === "BRAVO" || lobbyId.startsWith("TEAM")) {
    return "teams";
  }
  if (lobbyId === "CHARLIE" || lobbyId.startsWith("CTF")) {
    return "ctf";
  }
  if (lobbyId.startsWith("MOBA")) {
    return "moba";
  }
  return "ffa";
}

function resolveLobbyTeamMode(row: { teamMode?: unknown; id: string }): string {
  if (typeof row.teamMode === "string" && row.teamMode.length > 0) {
    return row.teamMode;
  }
  return inferLobbyTeamModeFromId(row.id);
}

function resolveLobbyGameMode(row: { gameMode?: unknown }): string {
  if (typeof row.gameMode === "string" && row.gameMode.length > 0) {
    return row.gameMode;
  }
  return "standard";
}

function collectLobbyList(connection: DbConnection): RealtimeLobbySummary[] {
  const playersByLobby = new Map<
    string,
    Array<{ playerId: string; name: string }>
  >();
  const playerCountsByLobby = new Map<string, number>();
  for (const playerRow of connection.db.player.iter()) {
    const players = playersByLobby.get(playerRow.lobbyId) ?? [];
    players.push({ playerId: playerRow.playerId, name: playerRow.name });
    playersByLobby.set(playerRow.lobbyId, players);
    playerCountsByLobby.set(
      playerRow.lobbyId,
      (playerCountsByLobby.get(playerRow.lobbyId) ?? 0) + 1,
    );
  }

  return Array.from(connection.db.lobby.iter())
    .map((row) => {
      const createdAt = resolveCreatedAt(
        (row as { createdAt?: unknown }).createdAt,
      );
      const lobbyPlayers = playersByLobby.get(row.id) ?? [];
      const teamMode = resolveLobbyTeamMode(row);
      const gameMode = resolveLobbyGameMode(row);
      const host = [...lobbyPlayers].sort((left, right) =>
        left.playerId.localeCompare(right.playerId),
      )[0];

      return {
        id: row.id,
        playerCount: playerCountsByLobby.get(row.id) ?? 0,
        maxPlayers: MAX_PER_LOBBY,
        spectatorCount: 0,
        status: toLobbyStatus(row.started, row.secondsUntilStart),
        secondsUntilStart: row.secondsUntilStart,
        createdAt,
        startTime: createdAt + 10_000,
        estimatedDuration: 180_000,
        hostUserId: host?.playerId,
        hostName: host?.name,
        schemaVersion: 1,
        gameMode,
        teamMode,
        gameConfig: {
          mode: gameMode,
          difficulty: "normal" as const,
        },
      };
    })
    .sort((left, right) => left.id.localeCompare(right.id));
}

function collectActiveLobbyState(
  connection: DbConnection,
  activeLobbyId: string | null,
): StdbLobbyStateEvent | null {
  if (!activeLobbyId) {
    return null;
  }

  const lobby = Array.from(connection.db.lobby.iter()).find(
    (row) => row.id === activeLobbyId,
  );
  if (!lobby) {
    return null;
  }

  const players = Array.from(connection.db.player.iter())
    .filter((row) => row.lobbyId === activeLobbyId)
    .map((row) => ({
      id: row.playerId,
      name: row.name,
      ...(typeof row.color === "string" ? { color: row.color } : {}),
      ready: true,
    }));

  return {
    lobbyId: activeLobbyId,
    players,
    started: lobby.started,
    secondsUntilStart: lobby.secondsUntilStart,
    teamMode: resolveLobbyTeamMode(lobby),
  };
}

function collectPartyState(
  connection: DbConnection,
  currentPlayerId: string | null,
): RealtimePartyState | null {
  if (!currentPlayerId) {
    return null;
  }

  const membership = connection.db.party_member.playerId.find(currentPlayerId);
  if (!membership) {
    return null;
  }

  const partyRow = connection.db.party.partyId.find(membership.partyId);
  if (!partyRow) {
    return null;
  }

  const members = Array.from(connection.db.party_member.iter())
    .filter((member) => member.partyId === partyRow.partyId)
    .map((member) => {
      const steamIdCandidate = (member as { steamId?: unknown }).steamId;
      return {
        id: member.playerId,
        name: member.name,
        steamId:
          typeof steamIdCandidate === "string" ? steamIdCandidate : undefined,
        ready: member.ready,
      };
    });

  return {
    id: partyRow.partyId,
    joinCode: partyRow.joinCode,
    leader: partyRow.leaderPlayerId,
    members,
  };
}

function toRealtimeGamePlayer(row: {
  playerId: string;
  name: string;
  isBot?: boolean;
  color?: string;
  posX?: number;
  posY?: number;
  velX?: number;
  velY?: number;
  rotation?: number;
  targetRotation?: number;
  angularVelocity?: number;
  health?: number;
  maxHealth?: number;
  kills?: number;
  deaths?: number;
  pelletsCollected?: number;
  scale?: number;
  damageMultiplier?: number;
  speedMultiplier?: number;
  lastAcknowledgedSequence?: number;
  totalKills?: number;
  totalDeaths?: number;
  totalPelletsCollected?: number;
  isDead?: boolean;
  deathSequence?: number;
  captures?: number;
  lastMineDeployAt?: number;
  lastBoostAt?: number;
  teamId?: number;
  primaryWeapon?: string;
}): RealtimeGamePlayer {
  const kills = row.kills ?? 0;
  const deaths = row.deaths ?? 0;
  const pelletsCollected = row.pelletsCollected ?? 0;

  return {
    id: row.playerId,
    name: row.name,
    ...(row.isBot === true ? { isBot: true } : {}),
    color: row.color ?? "#4ade80",
    pos: {
      x: row.posX ?? 0,
      y: row.posY ?? 0,
    },
    vel: {
      x: row.velX ?? 0,
      y: row.velY ?? 0,
    },
    rotation: row.rotation ?? 0,
    targetRotation: row.targetRotation ?? row.rotation ?? 0,
    angularVelocity: row.angularVelocity ?? 0,
    health: row.health ?? 100,
    maxHealth: row.maxHealth ?? 100,
    kills,
    deaths,
    pelletsCollected,
    scale: row.scale ?? 1,
    damageMultiplier: row.damageMultiplier ?? 1,
    speedMultiplier: row.speedMultiplier ?? 1,
    lastAcknowledgedSequence: row.lastAcknowledgedSequence ?? 0,
    totalKills: row.totalKills ?? kills,
    totalDeaths: row.totalDeaths ?? deaths,
    totalPelletsCollected: row.totalPelletsCollected ?? pelletsCollected,
    isDead: row.isDead ?? false,
    deathSequence: row.deathSequence ?? 0,
    captures: row.captures ?? 0,
    lastMineDeployAt: row.lastMineDeployAt ?? 0,
    lastBoostAt: row.lastBoostAt ?? 0,
    teamId:
      typeof row.teamId === "number" && row.teamId >= 0
        ? row.teamId
        : undefined,
    primaryWeapon: row.primaryWeapon ?? "DEFAULT",
  };
}

function toRealtimeGameBullet(row: {
  bulletId: string;
  ownerId: string;
  posX?: number;
  posY?: number;
  velX?: number;
  velY?: number;
  age?: number;
  shooterScale?: number;
  damage?: number;
}): RealtimeGameBullet {
  return {
    id: row.bulletId,
    ownerId: row.ownerId,
    pos: { x: row.posX ?? 0, y: row.posY ?? 0 },
    vel: { x: row.velX ?? 0, y: row.velY ?? 0 },
    age: row.age ?? 0,
    shooterScale: row.shooterScale ?? 1,
    damage: row.damage ?? 0,
  };
}

function toRealtimeGameMine(row: {
  mineId: string;
  ownerId?: string;
  posX?: number;
  posY?: number;
  velX?: number;
  velY?: number;
  health?: number;
  maxHealth?: number;
  deployTime?: number;
  radius?: number;
}): RealtimeGameMine {
  const normalizedDeployTime =
    typeof row.deployTime === "number" && row.deployTime > 10_000_000
      ? row.deployTime
      : Date.now();

  return {
    id: row.mineId,
    ownerId: row.ownerId ?? undefined,
    pos: { x: row.posX ?? 0, y: row.posY ?? 0 },
    vel: { x: row.velX ?? 0, y: row.velY ?? 0 },
    health: row.health ?? 100,
    maxHealth: row.maxHealth ?? 100,
    deployTime: normalizedDeployTime,
    radius: row.radius ?? 12,
  };
}

function toRealtimeGameAsteroid(row: {
  asteroidId: string;
  posX?: number;
  posY?: number;
  velX?: number;
  velY?: number;
  sizeLevel?: number;
  rotation?: number;
  rotationSpeed?: number;
  health?: number;
  maxHealth?: number;
}): RealtimeGameAsteroid {
  return {
    id: row.asteroidId,
    pos: { x: row.posX ?? 0, y: row.posY ?? 0 },
    vel: { x: row.velX ?? 0, y: row.velY ?? 0 },
    sizeLevel: row.sizeLevel ?? 0,
    rotation: row.rotation ?? 0,
    rotationSpeed: row.rotationSpeed ?? 0,
    health: row.health ?? 100,
    maxHealth: row.maxHealth ?? 100,
  };
}

function toRealtimeGamePellet(row: {
  pelletId: string;
  posX?: number;
  posY?: number;
  velX?: number;
  velY?: number;
  age?: number;
  value?: number;
  lifetime?: number;
}): RealtimeGamePellet {
  return {
    id: row.pelletId,
    pos: { x: row.posX ?? 0, y: row.posY ?? 0 },
    vel: { x: row.velX ?? 0, y: row.velY ?? 0 },
    age: row.age ?? 0,
    value: row.value ?? 1,
    lifetime: row.lifetime ?? 30,
  };
}

function toRealtimeMobaBase(row: {
  baseId: string;
  teamId: number;
  posX: number;
  posY: number;
  health: number;
  maxHealth: number;
  radius: number;
}): RealtimeMobaBase {
  return {
    id: row.baseId,
    teamId: row.teamId,
    pos: { x: row.posX, y: row.posY },
    health: row.health,
    maxHealth: row.maxHealth,
    radius: row.radius,
  };
}

function toRealtimeMobaNpc(row: {
  npcId: string;
  teamId: number;
  laneId: string;
  posX: number;
  posY: number;
  velX: number;
  velY: number;
  health: number;
  maxHealth: number;
  radius: number;
}): RealtimeMobaNpc {
  return {
    id: row.npcId,
    teamId: row.teamId,
    laneId: row.laneId,
    pos: { x: row.posX, y: row.posY },
    vel: { x: row.velX, y: row.velY },
    health: row.health,
    maxHealth: row.maxHealth,
    radius: row.radius,
  };
}

function normalizeFlagState(value: unknown): "home" | "carried" | "dropped" {
  if (value === "carried") {
    return "carried";
  }
  if (value === "dropped") {
    return "dropped";
  }
  return "home";
}

function collectActiveGameState(
  connection: DbConnection,
  activeLobbyId: string | null,
): StdbGameStateEvent | null {
  if (!activeLobbyId) {
    return null;
  }

  const players = Array.from(connection.db.player.iter())
    .filter((row) => row.lobbyId === activeLobbyId)
    .map((row) => toRealtimeGamePlayer(row));

  if (players.length === 0) {
    return null;
  }

  const bullets = Array.from(connection.db.bullet.iter())
    .filter((row) => row.lobbyId === activeLobbyId)
    .map((row) => toRealtimeGameBullet(row));

  const asteroids = Array.from(connection.db.asteroid.iter())
    .filter((row) => row.lobbyId === activeLobbyId)
    .map((row) => toRealtimeGameAsteroid(row));

  const pellets = Array.from(connection.db.pellet.iter())
    .filter((row) => row.lobbyId === activeLobbyId)
    .map((row) => toRealtimeGamePellet(row));

  const mines = Array.from(connection.db.mine.iter())
    .filter((row) => row.lobbyId === activeLobbyId)
    .map((row) => toRealtimeGameMine(row));
  const lobbyRow = Array.from(connection.db.lobby.iter()).find(
    (row) => row.id === activeLobbyId,
  );
  const gameMode = lobbyRow ? resolveLobbyGameMode(lobbyRow) : "standard";
  const teamScores: Record<number, number> =
    gameMode === "teams" || gameMode === "ctf" || gameMode === "moba"
      ? {
          0: lobbyRow?.team0Score ?? 0,
          1: lobbyRow?.team1Score ?? 0,
        }
      : {};
  const flags = Array.from(connection.db.ctf_flag?.iter?.() ?? [])
    .filter((row) => row.lobbyId === activeLobbyId)
    .map((row) => {
      const state = normalizeFlagState((row as { state?: unknown }).state);
      return {
        id: row.flagId,
        teamId: row.teamId,
        pos: { x: row.posX, y: row.posY },
        state,
        carrierId:
          state === "carried" && row.carrierId.length > 0
            ? row.carrierId
            : undefined,
        droppedAt:
          state === "dropped" && row.droppedAtSequence > 0
            ? row.droppedAtSequence
            : undefined,
      };
    });
  const mobaBases = Array.from(connection.db.moba_base?.iter?.() ?? [])
    .filter((row) => row.lobbyId === activeLobbyId)
    .map((row) => toRealtimeMobaBase(row));
  const mobaNpcs = Array.from(connection.db.moba_npc?.iter?.() ?? [])
    .filter((row) => row.lobbyId === activeLobbyId)
    .map((row) => toRealtimeMobaNpc(row));
  const lobbyMobaWaveNumber =
    typeof lobbyRow?.mobaWaveNumber === "number" ? lobbyRow.mobaWaveNumber : 0;

  const performance: RealtimePerformanceMetrics = {
    tps: 60,
    frameTime: 16.67,
    entities: {
      players: players.length,
      bullets: bullets.length,
      asteroids: asteroids.length,
      pellets: pellets.length,
    },
  };

  return {
    players,
    bullets,
    asteroids,
    pellets,
    mines,
    timestamp: Date.now(),
    sequence: Date.now(),
    gameModeState: {
      mode: gameMode,
      teamScores,
      captureLimit: lobbyRow?.captureLimit,
      flags,
    },
    ...(gameMode === "moba"
      ? {
          mobaState: {
            bases: mobaBases,
            npcs: mobaNpcs,
            waveNumber: lobbyMobaWaveNumber,
            teamScores,
          },
        }
      : {}),
    performance,
  };
}

function attachCacheListeners(
  connection: DbConnection,
  sync: () => void,
): void {
  connection.db.lobby.onInsert(() => sync());
  connection.db.lobby.onUpdate(() => sync());
  connection.db.lobby.onDelete(() => sync());
  connection.db.player.onInsert(() => sync());
  connection.db.player.onUpdate(() => sync());
  connection.db.player.onDelete(() => sync());
  connection.db.bullet.onInsert(() => sync());
  connection.db.bullet.onUpdate(() => sync());
  connection.db.bullet.onDelete(() => sync());
  connection.db.asteroid.onInsert(() => sync());
  connection.db.asteroid.onUpdate(() => sync());
  connection.db.asteroid.onDelete(() => sync());
  connection.db.pellet.onInsert(() => sync());
  connection.db.pellet.onUpdate(() => sync());
  connection.db.pellet.onDelete(() => sync());
  connection.db.mine.onInsert(() => sync());
  connection.db.mine.onUpdate(() => sync());
  connection.db.mine.onDelete(() => sync());
  connection.db.party.onInsert(() => sync());
  connection.db.party.onUpdate(() => sync());
  connection.db.party.onDelete(() => sync());
  connection.db.party_member.onInsert(() => sync());
  connection.db.party_member.onUpdate(() => sync());
  connection.db.party_member.onDelete(() => sync());
  connection.db.ctf_flag?.onInsert?.(() => sync());
  connection.db.ctf_flag?.onUpdate?.(() => sync());
  connection.db.ctf_flag?.onDelete?.(() => sync());
  connection.db.moba_base?.onInsert?.(() => sync());
  connection.db.moba_base?.onUpdate?.(() => sync());
  connection.db.moba_base?.onDelete?.(() => sync());
  connection.db.moba_npc?.onInsert?.(() => sync());
  connection.db.moba_npc?.onUpdate?.(() => sync());
  connection.db.moba_npc?.onDelete?.(() => sync());
  connection.db.game_event?.onInsert?.(() => sync());
  connection.db.game_event?.onDelete?.(() => sync());
}

function requireConnection(
  connection: DbConnection | null,
): asserts connection is DbConnection {
  if (!connection) {
    throw new Error("SpaceTimeDB connection not initialized");
  }
}

function createReducerCommandHandlers(params: {
  getConnection: () => DbConnection | null;
  setActiveLobbyId: (lobbyId: string | null) => void;
}) {
  return {
    async leaveLobby(): Promise<void> {
      const connection = params.getConnection();
      requireConnection(connection);
      params.setActiveLobbyId(null);
      connection.reducers.leaveLobby({});
    },

    async debugPelletBoost(): Promise<void> {
      const connection = params.getConnection();
      requireConnection(connection);
      connection.reducers.debugPelletBoost({});
    },

    async debugToggleInvincibility(): Promise<void> {
      const connection = params.getConnection();
      requireConnection(connection);
      connection.reducers.debugToggleInvincibility({});
    },

    async debugInstantDeath(): Promise<void> {
      const connection = params.getConnection();
      requireConnection(connection);
      connection.reducers.debugInstantDeath({});
    },

    async debugInstantVictory(): Promise<void> {
      const connection = params.getConnection();
      requireConnection(connection);
      connection.reducers.debugInstantVictory({});
    },

    async joinLobby(request: {
      lobbyId: string;
      playerName: string;
      primaryWeapon?: string;
    }): Promise<void> {
      const connection = params.getConnection();
      requireConnection(connection);
      const normalizedLobbyId = request.lobbyId.trim().toUpperCase();
      params.setActiveLobbyId(normalizedLobbyId);
      connection.reducers.joinLobby({
        lobbyId: normalizedLobbyId,
        playerName: request.playerName,
        primaryWeapon: request.primaryWeapon ?? "DEFAULT",
      });
    },

    async setLoadout(request: { primaryWeapon: string }): Promise<void> {
      const connection = params.getConnection();
      requireConnection(connection);
      connection.reducers.setLoadout({
        primaryWeapon: request.primaryWeapon,
      });
    },

    async deployMine(): Promise<void> {
      const connection = params.getConnection();
      requireConnection(connection);
      connection.reducers.deployMine({});
    },

    async submitInput(request: {
      lobbyId: string;
      sequence: number;
      input: {
        keyForward: boolean;
        keyBack: boolean;
        keyLeft: boolean;
        keyRight: boolean;
        keyBoost: boolean;
        keyShift: boolean;
        mouseScreenX: number;
        mouseScreenY: number;
        canvasWidth: number;
        canvasHeight: number;
        mouseShoot: boolean;
      };
    }): Promise<void> {
      const connection = params.getConnection();
      requireConnection(connection);
      connection.reducers.submitInput({
        lobbyId: request.lobbyId.trim().toUpperCase(),
        sequence: request.sequence,
        keyForward: request.input.keyForward,
        keyBack: request.input.keyBack,
        keyLeft: request.input.keyLeft,
        keyRight: request.input.keyRight,
        keyBoost: request.input.keyBoost,
        keyShift: request.input.keyShift,
        mouseShoot: request.input.mouseShoot,
        mouseScreenX: Math.round(request.input.mouseScreenX),
        mouseScreenY: Math.round(request.input.mouseScreenY),
        canvasWidth: Math.max(0, Math.round(request.input.canvasWidth)),
        canvasHeight: Math.max(0, Math.round(request.input.canvasHeight)),
      });
    },

    async partyCreate(request: { playerName: string }): Promise<void> {
      const connection = params.getConnection();
      requireConnection(connection);
      connection.reducers.partyCreate({
        playerName: request.playerName,
      });
    },

    async partyJoinCode(request: {
      joinCode: string;
      playerName: string;
    }): Promise<void> {
      const connection = params.getConnection();
      requireConnection(connection);
      connection.reducers.partyJoinCode({
        joinCode: request.joinCode.trim().toUpperCase(),
        playerName: request.playerName,
      });
    },

    async partyToggleReady(): Promise<void> {
      const connection = params.getConnection();
      requireConnection(connection);
      connection.reducers.partyToggleReady({});
    },

    async partyLeave(): Promise<void> {
      const connection = params.getConnection();
      requireConnection(connection);
      connection.reducers.partyLeave({});
    },

    async partyQueue(): Promise<void> {
      const connection = params.getConnection();
      requireConnection(connection);
      connection.reducers.partyQueue({});
    },
  };
}

function getPersistedToken(): string | undefined {
  try {
    return localStorage.getItem("stdb_auth_token") ?? undefined;
  } catch {
    return undefined;
  }
}

function persistToken(token: string): void {
  try {
    localStorage.setItem("stdb_auth_token", token);
  } catch {
    // localStorage unavailable (e.g. test environment).
  }
}

const RECONNECT_MAX_ATTEMPTS = 5;
const RECONNECT_BASE_DELAY_MS = 1000;

const SUBSCRIPTION_QUERIES = [
  "SELECT * FROM lobby",
  "SELECT * FROM player",
  "SELECT * FROM bullet",
  "SELECT * FROM asteroid",
  "SELECT * FROM pellet",
  "SELECT * FROM mine",
  "SELECT * FROM ctf_flag",
  "SELECT * FROM moba_base",
  "SELECT * FROM moba_npc",
  "SELECT * FROM party",
  "SELECT * FROM party_member",
  "SELECT * FROM game_event",
];

interface ConnectionCallbackParams {
  syncFromCache: () => void;
  scheduleSyncFromCache: () => void;
  setConnection: (connection: DbConnection | null) => void;
  setCurrentPlayerId: (playerId: string | null) => void;
  setSubscriptionHandle: (handle: { unsubscribe(): void } | null) => void;
  connectedListeners: Set<(event: StdbConnectedEvent) => void>;
}

function handleConnectionEstablished(
  params: ConnectionCallbackParams,
  nextConnection: DbConnection,
  identity: { toHexString(): string },
  connectionToken: string,
): void {
  params.setConnection(nextConnection);
  attachCacheListeners(nextConnection, params.scheduleSyncFromCache);
  params.setSubscriptionHandle(
    nextConnection
      .subscriptionBuilder()
      .onApplied(() => params.syncFromCache())
      .onError(() => {})
      .subscribe(SUBSCRIPTION_QUERIES),
  );

  const identityHex = identity.toHexString();
  params.setCurrentPlayerId(identityHex);
  persistToken(connectionToken);
  params.syncFromCache();
  emitToListeners(params.connectedListeners, {
    identity: identityHex,
    token: connectionToken,
  });
}

function scheduleReconnect(params: {
  config: StdbConnectionConfig;
  callbackParams: ConnectionCallbackParams;
  getConnection: () => DbConnection | null;
  disconnectedListeners: Set<() => void>;
  onReconnectAttempt?: (attempt: number, maxAttempts: number) => void;
  onReconnectSuccess?: () => void;
  onReconnectExhausted?: () => void;
}): void {
  let reconnectAttempt = 0;

  const attemptReconnect = () => {
    reconnectAttempt += 1;
    if (reconnectAttempt > RECONNECT_MAX_ATTEMPTS) {
      console.error(
        `Reconnect failed after ${RECONNECT_MAX_ATTEMPTS} attempts`,
      );
      params.onReconnectExhausted?.();
      for (const listener of params.disconnectedListeners) {
        listener();
      }
      return;
    }

    const delay = RECONNECT_BASE_DELAY_MS * 2 ** (reconnectAttempt - 1);
    console.log(
      `Reconnect attempt ${reconnectAttempt}/${RECONNECT_MAX_ATTEMPTS} in ${delay}ms`,
    );
    params.onReconnectAttempt?.(reconnectAttempt, RECONNECT_MAX_ATTEMPTS);

    setTimeout(() => {
      const savedToken = getPersistedToken();
      try {
        params.getConnection()?.disconnect();
      } catch {
        // Ignore disconnect failures during reconnect.
      }
      params.callbackParams.setConnection(null);

      params.callbackParams.setConnection(
        DbConnection.builder()
          .withUri(params.config.uri)
          .withDatabaseName(params.config.database)
          .withToken(savedToken)
          .onConnect(
            (
              conn: DbConnection,
              id: { toHexString(): string },
              tok: string,
            ) => {
              handleConnectionEstablished(params.callbackParams, conn, id, tok);
              params.onReconnectSuccess?.();
              console.log(
                `Reconnected successfully on attempt ${reconnectAttempt}`,
              );
            },
          )
          .onDisconnect(() => {
            params.callbackParams.setSubscriptionHandle(null);
            attemptReconnect();
          })
          .onConnectError((_ctx: unknown, err: Error) => {
            console.warn(`Reconnect attempt ${reconnectAttempt} failed:`, err);
            attemptReconnect();
          })
          .build(),
      );
    }, delay);
  };

  attemptReconnect();
}

async function connectWithTokenRecovery(params: {
  config: StdbConnectionConfig;
  syncFromCache: () => void;
  scheduleSyncFromCache: () => void;
  getConnection: () => DbConnection | null;
  setConnection: (connection: DbConnection | null) => void;
  setCurrentPlayerId: (playerId: string | null) => void;
  setSubscriptionHandle: (handle: { unsubscribe(): void } | null) => void;
  connectedListeners: Set<(event: StdbConnectedEvent) => void>;
  disconnectedListeners: Set<() => void>;
  connectErrorListeners: Set<(error: Error) => void>;
}): Promise<void> {
  const initialToken = normalizeConnectionToken(params.config.token);
  const callbackParams: ConnectionCallbackParams = {
    syncFromCache: params.syncFromCache,
    scheduleSyncFromCache: params.scheduleSyncFromCache,
    setConnection: params.setConnection,
    setCurrentPlayerId: params.setCurrentPlayerId,
    setSubscriptionHandle: params.setSubscriptionHandle,
    connectedListeners: params.connectedListeners,
  };

  await new Promise<void>((resolve, reject) => {
    let settled = false;
    let retriedWithoutToken = false;

    const resolveOnce = () => {
      if (!settled) {
        settled = true;
        resolve();
      }
    };

    const rejectOnce = (error: Error) => {
      if (!settled) {
        settled = true;
        reject(error);
      }
    };

    const buildConnection = (token?: string): void => {
      params.setConnection(
        DbConnection.builder()
          .withUri(params.config.uri)
          .withDatabaseName(params.config.database)
          .withToken(token)
          .onConnect(
            (
              conn: DbConnection,
              id: { toHexString(): string },
              tok: string,
            ) => {
              handleConnectionEstablished(callbackParams, conn, id, tok);
              resolveOnce();
            },
          )
          .onDisconnect(() => {
            params.setSubscriptionHandle(null);
            scheduleReconnect({
              config: params.config,
              callbackParams,
              getConnection: params.getConnection,
              disconnectedListeners: params.disconnectedListeners,
            });
          })
          .onConnectError((_ctx: unknown, error: Error) => {
            if (
              token &&
              !retriedWithoutToken &&
              isInvalidTokenConnectError(error)
            ) {
              retriedWithoutToken = true;
              try {
                params.getConnection()?.disconnect();
              } catch {
                // Ignore disconnect failures while retrying connect.
              }
              params.setConnection(null);
              buildConnection(undefined);
              return;
            }

            console.error("SpaceTimeDB connect error", error);
            for (const listener of params.connectErrorListeners) {
              listener(error);
            }
            rejectOnce(error);
          })
          .build(),
      );
    };

    buildConnection(initialToken);
  });
}

export function createStdbBindingsBridge(): StdbBindingsBridge {
  let connection: DbConnection | null = null;
  let activeLobbyId: string | null = null;
  let currentPlayerId: string | null = null;
  let subscriptionHandle: { unsubscribe(): void } | null = null;
  let gameStateSequence = 0;

  const connectedListeners = new Set<(event: StdbConnectedEvent) => void>();
  const disconnectedListeners = new Set<() => void>();
  const connectErrorListeners = new Set<(error: Error) => void>();
  const lobbyStateListeners = new Set<(event: StdbLobbyStateEvent) => void>();
  const lobbyListListeners = new Set<(event: StdbLobbyListEvent) => void>();
  const partyStateListeners = new Set<(event: StdbPartyStateEvent) => void>();
  const gameStateListeners = new Set<(event: StdbGameStateEvent) => void>();
  const commandHandlers = createReducerCommandHandlers({
    getConnection: () => connection,
    setActiveLobbyId: (lobbyId) => {
      activeLobbyId = lobbyId;
    },
  });

  let syncScheduled = false;
  const scheduleSyncFromCache = (): void => {
    if (syncScheduled) return;
    syncScheduled = true;
    const defer =
      typeof requestAnimationFrame === "function"
        ? requestAnimationFrame
        : (cb: FrameRequestCallback) => setTimeout(cb, 0);
    defer(() => {
      syncScheduled = false;
      syncFromCache();
    });
  };

  const syncFromCache = (): void => {
    if (!connection) {
      return;
    }

    if (currentPlayerId) {
      const selfPlayerRow = connection.db.player.playerId.find(currentPlayerId);
      if (selfPlayerRow) {
        activeLobbyId = selfPlayerRow.lobbyId;
      } else {
        activeLobbyId = null;
      }
    }

    const activeLobbyState = collectActiveLobbyState(connection, activeLobbyId);
    if (activeLobbyState) {
      emitToListeners(lobbyStateListeners, activeLobbyState);
    }

    emitToListeners(lobbyListListeners, {
      lobbies: collectLobbyList(connection),
    });

    emitToListeners(partyStateListeners, {
      party: collectPartyState(connection, currentPlayerId),
    });

    const activeGameState = collectActiveGameState(connection, activeLobbyId);
    if (activeGameState) {
      gameStateSequence += 1;
      emitToListeners(gameStateListeners, {
        ...activeGameState,
        sequence: gameStateSequence,
      });
    }
  };

  return {
    async connect(config: StdbConnectionConfig): Promise<void> {
      if (connection) {
        connection.disconnect();
        connection = null;
      }
      await connectWithTokenRecovery({
        config,
        syncFromCache,
        scheduleSyncFromCache,
        getConnection: () => connection,
        setConnection: (nextConnection) => {
          connection = nextConnection;
        },
        setCurrentPlayerId: (playerId) => {
          currentPlayerId = playerId;
        },
        setSubscriptionHandle: (handle) => {
          subscriptionHandle = handle;
        },
        connectedListeners,
        disconnectedListeners,
        connectErrorListeners,
      });
    },

    async disconnect(): Promise<void> {
      if (subscriptionHandle) {
        subscriptionHandle.unsubscribe();
        subscriptionHandle = null;
      }
      if (connection) {
        connection.disconnect();
        connection = null;
      }
      activeLobbyId = null;
      currentPlayerId = null;
      gameStateSequence = 0;
    },

    async requestLobbyList(): Promise<void> {
      syncFromCache();
    },

    ...commandHandlers,

    onConnected(handler: (event: StdbConnectedEvent) => void): () => void {
      connectedListeners.add(handler);
      return () => {
        connectedListeners.delete(handler);
      };
    },

    onDisconnected(handler: () => void): () => void {
      disconnectedListeners.add(handler);
      return () => {
        disconnectedListeners.delete(handler);
      };
    },

    onConnectError(handler: (error: Error) => void): () => void {
      connectErrorListeners.add(handler);
      return () => {
        connectErrorListeners.delete(handler);
      };
    },

    onLobbyState(handler: (event: StdbLobbyStateEvent) => void): () => void {
      lobbyStateListeners.add(handler);
      return () => {
        lobbyStateListeners.delete(handler);
      };
    },

    onLobbyList(handler: (event: StdbLobbyListEvent) => void): () => void {
      lobbyListListeners.add(handler);
      return () => {
        lobbyListListeners.delete(handler);
      };
    },

    onPartyState(handler: (event: StdbPartyStateEvent) => void): () => void {
      partyStateListeners.add(handler);
      return () => {
        partyStateListeners.delete(handler);
      };
    },

    onGameState(handler: (event: StdbGameStateEvent) => void): () => void {
      gameStateListeners.add(handler);
      return () => {
        gameStateListeners.delete(handler);
      };
    },
  };
}
